import { Injectable } from '@angular/core';
import { ChartConfiguration } from 'chart.js';
import _ from 'lodash';
import { MAIN_PAYMENT_METHODS_DROPDOWN } from '../constants';
import { Graphs, GraphType } from '../constants/practice-analytics.constant';
import {
  ChartConfigModel,
  ChartResp,
} from '../models/tenants/practice-analytics.models';
import { UtilService } from './util.service';

/** Inter font constant for Chart.js */
const INTER_FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
const GOOGLE_SANS_FONT = "'Google Sans', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
@Injectable({
  providedIn: 'root',
})
export class ChartService {
  constructor(public _utilService: UtilService) {
    if (window) {
      console.log("window.devicePixelRatio", window.devicePixelRatio)
    }
  }
  paymentMethods = MAIN_PAYMENT_METHODS_DROPDOWN;

  barOptions: ChartConfiguration<any>['options'] = {
    indexAxis: 'x',
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: {
        top: 30,
        right: 30,
        bottom: 10,
        left: 10,
      },
    },
    animation: {
      duration: 400,
      easing: 'easeOutQuart',
    },
    transitions: {
      active: {
        animation: {
          duration: 0,
        },
      },
    },
    interaction: {
      mode: 'nearest',
      intersect: true,
    },
    plugins: {
      datalabels: {
        display: true,
        font: {
          family: GOOGLE_SANS_FONT,
          size: 11,
          weight: '600',
        },
        color: '#1E2129',
      },
      legend: {
        display: false,
        labels: {
          usePointStyle: true,
          font: {
            family: GOOGLE_SANS_FONT,
            size: 12,
            weight: '500',
          },
          color: '#3F4254',
        },
      },
      tooltip: {
        usePointStyle: true,
        pointStyle: 'circle',
        backgroundColor: '#1F2937',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        borderColor: 'rgba(255, 255, 255, 0.15)',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 10,
        titleFont: {
          family: GOOGLE_SANS_FONT,
          size: 12,
          weight: '600',
        },
        bodyFont: {
          family: GOOGLE_SANS_FONT,
          size: 11,
          weight: '400',
        },
        callbacks: {
          label: '',
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#5E6278',
          font: {
            family: GOOGLE_SANS_FONT,
            size: 11,
            weight: '500',
          },
        },
        title: {
          display: true,
          align: 'center',
          text: '',
          color: '#1E2129',
          font: {
            family: GOOGLE_SANS_FONT,
            size: 13,
            weight: '600',
          },
          padding: { top: 10, bottom: 5, left: 0, right: 0 },
        },
        grid: {
          display: true,
          color: 'rgba(235, 238, 243, 0.7)',
          drawBorder: false,
        },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: '#5E6278',
          font: {
            family: GOOGLE_SANS_FONT,
            size: 11,
            weight: '500',
          },
        },
        title: {
          display: true,
          align: 'center',
          text: '',
          color: '#1E2129',
          font: {
            family: GOOGLE_SANS_FONT,
            size: 13,
            weight: '600',
          },
          padding: { top: 10, bottom: 5, left: 0, right: 0 },
        },
        grid: {
          color: 'rgba(235, 238, 243, 0.7)',
          drawBorder: false,
        },
      },
    },
    // responsive: true,
  };

  public lineOptions: ChartConfiguration<any>['options'] = {
    layout: {
      padding: {
        top: 30,
        right: 30,
      },
    },
    animation: {
      duration: 400,
      easing: 'easeOutQuart',
    },
    transitions: {
      active: {
        animation: {
          duration: 0,
        },
      },
    },
    plugins: {
      datalabels: {
        display: true,
        anchor: 'end',
        align: 'top',
        backgroundColor: '#FFFFFF',
        borderColor: '#DAE1E5',
        borderWidth: 1,
        borderRadius: 4,
        color: '#1E2129',
        font: {
          family: GOOGLE_SANS_FONT,
          size: 10,
          weight: '600',
        },
      },
      legend: {
        display: false,
      },
      tooltip: {
        usePointStyle: true,
        pointStyle: 'circle',
        backgroundColor: '#1F2937',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        borderColor: 'rgba(255, 255, 255, 0.15)',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 10,
        titleFont: {
          family: GOOGLE_SANS_FONT,
          size: 12,
          weight: '600',
        },
        bodyFont: {
          family: GOOGLE_SANS_FONT,
          size: 11,
          weight: '400',
        },
        callbacks: {
          label: '',
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#5E6278',
          font: {
            family: GOOGLE_SANS_FONT,
            size: 11,
            weight: '500',
          },
        },
        title: {
          display: true,
          align: 'center',
          text: '',
          color: '#1E2129',
          font: {
            family: GOOGLE_SANS_FONT,
            size: 13,
            weight: '600',
          },
        },
        grid: {
          display: false,
        },
      },
      y: {
        ticks: {
          color: '#5E6278',
          font: {
            family: GOOGLE_SANS_FONT,
            size: 11,
            weight: '500',
          },
        },
        title: {
          display: true,
          align: 'center',
          text: '',
          color: '#1E2129',
          font: {
            family: GOOGLE_SANS_FONT,
            size: 13,
            weight: '600',
          },
        },
        grid: {
          color: 'rgba(235, 238, 243, 0.7)',
          drawBorder: false,
        },
      },
    },
    responsive: true,
  };

  public doughnutOptions: ChartConfiguration<any>['options'] = {
    devicePixelRatio: typeof window !== 'undefined' ? Math.max(window.devicePixelRatio || 1, 2) : 2,
    // cutout: '68%',
    animation: {
      duration: 400,
      easing: 'easeOutQuart',
    },
    transitions: {
      active: {
        animation: {
          duration: 0,
        },
      },
    },
    plugins: {
      datalabels: {
        display: false,
      },
      legend: {
        position: 'bottom',
        display: true,
        labels: {
          font: {
            family: GOOGLE_SANS_FONT,
            size: 12,
            weight: '500',
          },
          usePointStyle: true,
          pointStyle: 'circle',
          color: '#3F4254',
          padding: 20,
        },
        onHover: function (event: any) {
          if (event?.native?.target) {
            event.native.target.style.cursor = 'pointer';
          }
        },
        onLeave: function (event: any) {
          if (event?.native?.target) {
            event.native.target.style.cursor = 'default';
          }
        },
      },
      tooltip: {
        backgroundColor: '#1E2129',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        borderColor: 'rgba(255, 255, 255, 0.15)',
        borderWidth: 1,
        cornerRadius: 8,
        padding: 10,
        callbacks: {
          label: '',
        },
      },
    },
    responsive: true,
  };

  public treeMapOption: ChartConfiguration<any>['options'] = {};

  public bartData: ChartConfiguration<any>['data'] = {
    labels: [],
    datasets: [
      {
        axis: '',
        data: [],
        backgroundColor: ['#2491EB'],
        // borderRadius: 6,
        // // maxBarThickness: 28,
      },
    ],
  };

  public lineData: ChartConfiguration<any>['data'] = {
    labels: [],
    datasets: [
      {
        data: [],
        label: '',
        fill: true,
        tension: 0.35,
        borderColor: '#2491EB',
        backgroundColor: 'rgba(24, 139, 204, 0.08)',
        pointRadius: 4,
        pointHoverRadius: 6,
        pointBackgroundColor: '#FFFFFF',
        pointBorderColor: '#2491EB',
        pointBorderWidth: 2,
      },
    ],
  };

  public doughnutData: ChartConfiguration<any>['data'] = {
    labels: [],
    datasets: [
      {
        data: [],
        label: '',
        backgroundColor: [
          '#2491EB',
          '#50CD89',
          '#F7B42E',
          '#7239EA',
          '#00C2FF',
          '#F1416C',
        ],
        hoverOffset: 6,
      },
    ],
  };

  isGraphHaveAmount(id: number): boolean {
    return (
      id == Graphs.CCPaymentTrends ||
      id == Graphs.OverallTrends ||
      id == Graphs.GrossProduction ||
      id == Graphs.NetProduction ||
      id == Graphs.NetCollections ||
      id == Graphs.Collection ||
      id == Graphs.Production_Service ||
      id == Graphs.TotalAR ||
      id == Graphs.Collection_byCarrier ||
      id == Graphs.ARAbove90d ||
      id == Graphs.Production_PerDay ||
      id == Graphs.ARBreakdown ||
      id == Graphs.TotalAdjustment ||
      id == Graphs.CollectionByProviderTypes ||
      id == Graphs.ProductionByProviderTypes ||
      id == Graphs.Adjustment_byProcedureType ||
      id == Graphs.Adjustment_byProvider ||
      id == Graphs.ProviderProduction_PerDay ||
      id == Graphs.HygieneProduction_PerDay ||
      id == Graphs.OrthoProduction_PerDay ||
      id == Graphs.Production_ProcedureCategory ||
      id == Graphs.CollectionByProvier ||
      id == Graphs.ProductionByProvider
    );
  }

  barChart(data: ChartResp, id: number, type: number) {
    let model = this.getChartModel(data);
    let datasource = JSON.parse(JSON.stringify(this.bartData));
    let options = JSON.parse(JSON.stringify(this.barOptions));
    datasource.labels = model.labels;
    datasource.datasets[0].data = model.data;
    if (model.xLabel != '') {
      options.scales.x.title.text =
        type != null && type == GraphType.HorizontalBar
          ? model.yLabel
          : model.xLabel;
    }
    if (model.yLabel != '') {
      options.scales.y.title.text =
        type != null && type == GraphType.HorizontalBar
          ? model.xLabel
          : model.yLabel;
    }

    if (this.isGraphHaveAmount(id)) {
      if (type != null && type == GraphType.HorizontalBar) {
        options.scales.x.ticks = {
          callback: function (value: any) {
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(value);
          },
        };
      } else {
        options.scales.y.ticks = {
          callback: function (value: any) {
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(value);
          },
        };
      }

      options.plugins.datalabels.formatter = function (value: any) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(value); // Display the raw value.
      };

      options.plugins.tooltip.callbacks.label = function (context: any) {
        let val = 0;
        if (context.dataset.axis == 'Y') {
          val = context.parsed.x;
        } else {
          val = context.parsed.y;
        }

        let label = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(val);
        return label;
      };
    } else {
      options.plugins.tooltip.callbacks.label = function (context: any) {
        let label = context.dataset.label || '';

        if (label) {
          label += ': ';
        }
        let val = 0;
        if (context.dataset.axis == 'Y') {
          val = context.parsed.x;
        } else {
          val = context.parsed.y;
        }

        label += val;
        return label;
      };
    }

    if (type == GraphType.VerticalBar) {
      datasource.datasets[0].backgroundColor = ['#2491EB'];
      datasource.datasets[0].borderRadius = 6;
      options.indexAxis = 'x';
      datasource.datasets[0].axis = 'X';
    }
    if (id == Graphs.ActivePatients) {
      // datasource.datasets[0].backgroundColor = ['#F7B42E'];
      datasource.datasets[0].borderRadius = 6;
    }

    if (type != null && type == GraphType.HorizontalBar) {
      datasource.datasets[0].backgroundColor = ['#2491EB'];
      datasource.datasets[0].borderRadius = 6;
      options.indexAxis = 'y';
      datasource.datasets[0].axis = 'Y';
    }

    if (
      id == Graphs.ARBreakdown ||
      id == Graphs.ProductionByProviderTypes ||
      id == Graphs.CollectionByProviderTypes
    ) {
      options.plugins.legend.display = true;
      options.plugins.legend.labels.usePointStyle = true;
    }
    if (id == Graphs.ARBreakdown) {
      datasource.datasets[0].label = '30d';
    }

    if (id == Graphs.ProductionByProviderTypes) {
      datasource.datasets[0].label = 'Hygienists';
    }
    if (id == Graphs.CollectionByProviderTypes) {
      datasource.datasets[0].label = 'Hygienists';
    }

    if (
      (model.data1 != null && model.data1.length > 0) ||
      (model.data2 != null && model.data2.length > 0)
    ) {
      options.scales.x.stacked = true;
      options.scales.y.stacked = true;
      options.plugins.datalabels.display = true;
      options.layout.padding.top = 0;
    }
    if (model.data1 != null && model.data1.length > 0) {
      let lab = '';
      if (id == Graphs.ARBreakdown) {
        lab = '60d';
      }
      if (id == Graphs.ProductionByProviderTypes) {
        lab = 'Providers';
      }
      if (id == Graphs.CollectionByProviderTypes) {
        lab = 'Providers';
      }
      datasource.datasets.push({
        data: model.data1,
        backgroundColor: '#d83131',
        borderRadius: 8,
        // maxBarThickness: 20,
        label: lab,
      });
    }
    if (model.data2 != null && model.data2.length > 0) {
      let lab = '';
      if (id == Graphs.ARBreakdown) {
        lab = '90d';
      }
      datasource.datasets.push({
        data: model.data2,
        backgroundColor: '#f7b42e',
        borderRadius: 8,
        label: lab,
        // maxBarThickness: 20,
      });
    }

    if (data.type === GraphType.Stacked) {
      let minMaxArray = _.flatten(
        data.series?.map((d: any) => {
          return [d.value, d.value1, d.value2];
        })
      );
      let maxY = minMaxArray.reduce((a, b) => Math.max(a, b));
      let minY = minMaxArray.reduce((a, b) => Math.min(a, b));
      datasource.datasets.forEach((c: any) => {
        c.axis = 'X';
      });
      datasource.datasets[0].backgroundColor = '#2491EB';
      options.plugins.datalabels.display = true;
      (options.scales.y.min = this._utilService.roundToNext(minY)),
        (options.scales.y.max = this._utilService.roundToNext(maxY)),
        (options.scales.y.grid = {
          display: true,
          color: 'rgba(235, 238, 243, 0.7)',
          drawBorder: false,
        }),
        (options.scales.x.grid = {
          display: false,
          offset: true,
          color: 'rgba(235, 238, 243, 0.7)',
          drawBorder: false,
        });
      options.scales.x.offset = true;
      options.scales.x.ticks = {
        ...options.scales.x.ticks,
        align: 'center',
      };
      options.layout = {
        padding: {
          top: 50,
          right: 30,
          bottom: 10,
          left: 10,
        },
      };
    }

    if (id === Graphs.ARBreakdown) {
      options.plugins.datalabels.align = 'center';
      options.plugins.datalabels.anchor = 'center';
      let minMaxArray = data.series?.map((d: any) => {
        return (
          (d.value > 0 ? d.value : 0) +
          (d.value1 > 0 ? d.value1 : 0) +
          (d.value2 > 0 ? d.value2 : 0)
        );
      });
      let minMaxArray2 = data.series?.map((d: any) => {
        const v = d.value < 0 ? d.value : 0;
        const v1 = d.value1 < 0 ? d.value1 : 0;
        const v2 = d.value2 < 0 ? d.value2 : 0;
        return v + v1 + v2; // sum of negatives (or zero)
      });
      let maxY = minMaxArray.reduce((a, b) => Math.max(a, b));
      let minY = minMaxArray2.reduce((a, b) => Math.min(a, b));
      options.scales.y.min = this._utilService.roundToNext(minY);
      options.scales.y.max = this._utilService.roundToNext(maxY);
    }

    return {
      type: 'bar',
      data: datasource,
      options: options,
    };
  }

  updateBar(chart: any, data: ChartResp, compare?: boolean) {
    let model = this.getChartModel(data);
    let config = this.getChartModel(data);

    chart.data = chart.data ?? {};
    chart.data.datasets = chart.data.datasets ?? [{}];
    chart.data.datasets[0].data = config.data;
    chart.data.labels = config.labels;
    if (config.xLabel != '') {
      chart.options.scales.x.title.text = config.xLabel;
    }
    if (config.yLabel != '') {
      chart.options.scales.y.title.text = config.yLabel;
    }

    if (
      (config.data1 != null && config.data1.length > 0) ||
      (config.data2 != null && config.data2.length > 0)
    ) {
      chart.options.scales.x.stacked = true;
      chart.options.scales.y.stacked = true;
      chart.options.scales.x.offset = true;
      if (chart.options.scales.x.grid) {
        chart.options.scales.x.grid.offset = true;
      }
    }

    if (config.data1 != null && config.data1.length > 0) {
      let lab = '';
      if (chart.data.datasets[1]) {
        lab = chart.data.datasets[1].label;
        chart.data.datasets.splice(1, 1);
      }
      chart.data.datasets.push({
        data: config.data1,
        backgroundColor: '#d83131',
        borderRadius: 8,
        // // maxBarThickness: 20,
        label: lab,
      });
    }
    if (config.data2 != null && config.data2.length > 0) {
      let lab = '';
      if (chart.data.datasets[1]) {
        lab = chart.data.datasets[1].label;
        chart.data.datasets.splice(1, 1);
      }
      chart.data.datasets.push({
        data: config.data2,
        backgroundColor: '#f7b42e',
        borderRadius: 8,
        // // maxBarThickness: 20,
        label: lab,
      });
    }

    chart.config._config.options.plugins.tooltip = {
      usePointStyle: true,
      pointStyle: 'circle',
      backgroundColor: '#1F2937',
      bodyColor: '#FFFFFF',
      borderColor: 'rgba(255, 255, 255, 0.15)',
      borderWidth: 1,
      callbacks: {
        label: {},
      },
      cornerRadius: 8,
      titleColor: '#FFFFFF',
    };
    // compare code
    if (compare) {
      if (data.type === GraphType.VerticalBar || data.type === GraphType.Bar) {
        chart.config._config.data.labels = config.labels.splice(
          0,
          Number(config.labels?.length || 0) / 2
        );
        chart.config._config.data.datasets = [
          {
            label: data.seriesLabel,
            data: data.series.map((x) => x.value),
            backgroundColor: '#2491EB',
            borderColor: '#2491EB',
            borderWidth: 1,
            borderRadius: 6,
            // // maxBarThickness: 20,
            // Store the current year labels
            originalLabels: data.series.map((x) => x.key), // Store as-is from backend
          },
          {
            label: data.previousYearSeriesLabel,
            data: data.previousYearSeries?.map((x) => x.value),
            backgroundColor: '#F1416C',
            borderColor: '#F1416C',
            borderWidth: 1,
            borderRadius: 6,
            // // maxBarThickness: 20,
            // Store the previous year labels
            originalLabels: data.previousYearSeries?.map((x) => x.key), // Store as-is from backend
          },
        ];
        chart.config._config.options = {
          animation: {
            duration: 400,
            easing: 'easeOutQuart',
          },
          transitions: {
            active: {
              animation: {
                duration: 0,
              },
            },
          },
          responsive: true,
          plugins: {
            datalabels: {
              align: 'top',
              anchor: 'end',
              rotation: -90,
              backgroundColor: '#fff',
              borderColor: '#DAE1E5',
              borderRadius: 4,
              borderWidth: 0,
              color: '#6d7c85', // bar chart label color
              display: true,
              font: {
                size: '12', // bar chart label size
                weight: '600', // bar chart label weight
              },
            },
            legend: {
              position: 'top',
              display: true,
              labels: {
                usePointStyle: false,
              },
            },
            tooltip: {
              usePointStyle: true,
              pointStyle: 'circle',
              backgroundColor: '#1F2937',
              titleColor: '#FFFFFF',
              bodyColor: '#FFFFFF',
              borderColor: 'rgba(255, 255, 255, 0.15)',
              borderWidth: 1,
              cornerRadius: 8,
              callbacks: {
                title: function (context: any) {
                  // Use the dataset's original labels instead of the shared x-axis labels
                  const datasetIndex = context[0].datasetIndex;
                  const dataIndex = context[0].dataIndex;
                  const dataset = context[0].chart.data.datasets[datasetIndex];
                  if (
                    dataset.originalLabels &&
                    dataset.originalLabels[dataIndex]
                  ) {
                    return dataset.originalLabels[dataIndex];
                  }
                  return context[0].label;
                },
              },
            },
          },
          scales: {
            y: {
              beginAtZero: false,
              title: {
                display: true,
                text: model.yLabel,
                color: '#000',
                font: {
                  size: 16,
                  weight: 'bold',
                },
                padding: {
                  top: 10,
                  bottom: 5,
                  left: 0,
                  right: 0,
                },
              },
              grid: {
                color: 'rgba(0,0,0,0.1)',
                display: false,
                drawOnChartArea: true,
                drawTicks: true,
                lineWidth: 1,
                offset: false,
                tickLength: 8,
              },
              ticks: {
                color: 'black',
              },
            },
            x: {
              title: {
                display: true,
                text: model.xLabel,
                color: '#000',
                font: {
                  size: 16,
                  weight: 'bold',
                },
                padding: {
                  top: 10,
                  bottom: 5,
                  left: 0,
                  right: 0,
                },
              },
              grid: {
                color: 'rgba(0,0,0,0.1)',
                display: false,
                drawOnChartArea: true,
                drawTicks: true,
                lineWidth: 1,
                offset: false,
                tickLength: 8,
              },
            },
          },
        };
      }
      if (data.type === GraphType.HorizontalBar) {
        chart.config._config.data.labels = config.labels.splice(
          0,
          Number(config.labels?.length || 0) / 2
        );
        chart.config._config.data.datasets = [
          {
            label: data.seriesLabel,
            data: data.series.map((x) => x.value),
            backgroundColor: '#2491EB',
            borderColor: '#2491EB',
            borderWidth: 1,
            borderRadius: 6,
            // // maxBarThickness: 20,
            originalLabels: data.series.map((x) => x.key),
          },
          {
            label: data.previousYearSeriesLabel,
            data: data.previousYearSeries?.map((x) => x.value),
            backgroundColor: '#F1416C',
            borderColor: '#F1416C',
            borderWidth: 1,
            borderRadius: 6,
            // // maxBarThickness: 20,
            originalLabels: data.previousYearSeries?.map((x) => x.key),
          },
        ];

        chart.config._config.options = {
          animation: {
            duration: 400,
            easing: 'easeOutQuart',
          },
          transitions: {
            active: {
              animation: {
                duration: 0,
              },
            },
          },
          responsive: true,
          indexAxis: 'y',
          plugins: {
            datalabels: {
              align: 'center',
              anchor: 'end',
              // rotation: -90,
              backgroundColor: '#fff',
              borderColor: '#DAE1E5',
              borderRadius: 4,
              borderWidth: 0,
              color: '#6d7c85', // bar chart label color
              display: true,
              font: {
                size: '12', // bar chart label size
                weight: '600', // bar chart label weight
              },
            },
            legend: {
              position: 'top',
              display: true,
              labels: {
                usePointStyle: false,
              },
            },
            tooltip: {
              usePointStyle: true,
              pointStyle: 'circle',
              backgroundColor: '#1F2937',
              titleColor: '#FFFFFF',
              bodyColor: '#FFFFFF',
              borderColor: 'rgba(255, 255, 255, 0.15)',
              borderWidth: 1,
              cornerRadius: 8,
              callbacks: {
                title: function (context: any) {
                  const datasetIndex = context[0].datasetIndex;
                  const dataIndex = context[0].dataIndex;
                  const dataset = context[0].chart.data.datasets[datasetIndex];
                  return (
                    dataset.originalLabels?.[dataIndex] || context[0].label
                  );
                },
              },
            },
          },
          scales: {
            x: {
              // Now the "Amount" axis
              beginAtZero: false,
              title: {
                display: true,
                text: model.yLabel, // Amount or similar
                color: '#000',
                font: {
                  size: 16,
                  weight: 'bold',
                },
                padding: {
                  top: 10,
                  bottom: 5,
                  left: 0,
                  right: 0,
                },
                ticks: {
                  color: 'black',
                },
              },
              grid: {
                color: 'rgba(0,0,0,0.1)',
                display: false,
                drawOnChartArea: true,
                drawTicks: true,
                lineWidth: 1,
                offset: false,
                tickLength: 8,
              },
            },
            y: {
              title: {
                display: true,
                text: model.xLabel, // Categories like months or names
                color: '#000',
                font: {
                  size: 16,
                  weight: 'bold',
                },
                padding: {
                  top: 10,
                  bottom: 5,
                  left: 0,
                  right: 0,
                },
              },
              grid: {
                color: 'rgba(0,0,0,0.1)',
                display: false,
                drawOnChartArea: true,
                drawTicks: true,
                lineWidth: 1,
                offset: false,
                tickLength: 8,
              },
            },
          },
        };
      }
    } else {
      if (data.type === GraphType.VerticalBar || data.type === GraphType.Bar) {
        chart.config._config.options = {
          animation: {
            duration: 400,
            easing: 'easeOutQuart',
          },
          transitions: {
            active: {
              animation: {
                duration: 0,
              },
            },
          },
          indexAxis: 'x',
          responsive: true,
          scales: {
            x: {
              grid: {
                color: 'rgba(0,0,0,0.1)',
                display: false,
                drawOnChartArea: true,
                drawTicks: true,
                lineWidth: 1,
                offset: true,
                tickLength: 8,
              },
              title: {
                align: 'center',
                color: 'black',
                display: true,
                font: {
                  size: 16,
                  weight: 'bold',
                },
                padding: {
                  bottom: 5,
                  left: 0,
                  right: 0,
                  top: 10,
                },
                text: data.xlabel,
                ticks: {},
              },
            },
            y: {
              grid: {
                color: 'rgba(0,0,0,0.1)',
                display: false,
                drawOnChartArea: true,
                drawTicks: true,
                lineWidth: 1,
                offset: false,
                tickLength: 8,
              },
              title: {
                align: 'center',
                color: 'black',
                display: true,
                font: {
                  size: 16,
                  weight: 'bold',
                },
                padding: {
                  bottom: 5,
                  left: 0,
                  right: 0,
                  top: 10,
                },
                text: data.ylabel,
              },
            },
          },
          layout: {
            padding: {
              top: 50,
              right: 50,
            },
          },
          plugins: {
            datalabels: {
              align: 'top',
              anchor: 'end',
              rotation: -90,
              backgroundColor: '#fff',
              borderColor: '#DAE1E5',
              borderRadius: 4,
              borderWidth: 0,
              color: '#6d7c85', // bar chart label color
              display: true,
              font: {
                size: '12', // bar chart label size
                weight: '600', // bar chart label weight
              },
            },
            legend: {
              display: false,
              labels: {
                usePointStyle: false,
              },
            },
            tooltip: {
              usePointStyle: true,
              pointStyle: 'circle',
              backgroundColor: '#1F2937',
              bodyColor: '#FFFFFF',
              borderColor: 'rgba(255, 255, 255, 0.15)',
              borderWidth: 1,
              callbacks: {},
              cornerRadius: 8,
              titleColor: '#FFFFFF',
            },
          },
        };
        chart.data.datasets = [chart.data.datasets[0]];
      }
      if (data.type === GraphType.HorizontalBar) {
        chart.config._config.options = {
          animation: {
            duration: 400,
            easing: 'easeOutQuart',
          },
          transitions: {
            active: {
              animation: {
                duration: 0,
              },
            },
          },
          indexAxis: 'y',
          scales: {
            x: {
              title: {
                display: true,
                text: data.ylabel, // Amount or similar
                color: '#000',
                font: {
                  size: 16,
                  weight: 'bold',
                },
                padding: {
                  top: 10,
                  bottom: 5,
                  left: 0,
                  right: 0,
                },
                ticks: {
                  color: 'black',
                },
              },
              grid: {
                color: 'rgba(0,0,0,0.1)',
                display: false,
                drawOnChartArea: true,
                drawTicks: true,
                lineWidth: 1,
                offset: false,
                tickLength: 8,
              },
            },
            y: {
              title: {
                display: true,
                text: data.xlabel, // Amount or similar
                color: '#000',
                font: {
                  size: 16,
                  weight: 'bold',
                },
                padding: {
                  top: 10,
                  bottom: 5,
                  left: 0,
                  right: 0,
                },
                ticks: {
                  color: 'black',
                },
              },
              grid: {
                color: 'rgba(0,0,0,0.1)',
                display: false,
                drawOnChartArea: true,
                drawTicks: true,
                lineWidth: 1,
                offset: false,
                tickLength: 8,
              },
            },
          },
          layout: {
            padding: {
              top: 50,
              right: 50,
            },
          },
          plugins: {
            datalabels: {
              align: 'center',
              anchor: 'end',
              // rotation: -90,
              backgroundColor: '#fff',
              borderColor: '#DAE1E5',
              borderRadius: 4,
              borderWidth: 0,
              color: '#6d7c85', // bar chart label color
              display: true,
              font: {
                size: '12', // bar chart label size
                weight: '600', // bar chart label weight
              },
            },
            legend: {
              display: false,
              labels: {
                usePointStyle: false,
              },
            },
            tooltip: {
              usePointStyle: true,
              pointStyle: 'circle',
              backgroundColor: '#1F2937',
              bodyColor: '#FFFFFF',
              borderColor: 'rgba(255, 255, 255, 0.15)',
              borderWidth: 1,
              callbacks: {},
              cornerRadius: 8,
              titleColor: '#FFFFFF',
            },
          },
        };
        chart.data.datasets = [chart.data.datasets[0]];
        chart.data.datasets[0].backgroundColor = ['#2491EB'];
        chart.data.datasets[0].borderRadius = 6;
        chart.data.datasets[0].axis = 'Y';
      }
    }
    // chart.config._config.options.plugins.tooltip = {
    //   backgroundColor: '#fff',
    //   bodyColor: '#000',
    //   borderColor: '#cdcdcd',
    //   borderWidth: 1,
    //   cornerRadius: 4,
    //   titleColor: '#000',
    //   callbacks: {
    //     label: '',
    //   },
    // };
    if (this.isGraphHaveAmount(data.id)) {
      chart.config._config.options.plugins.datalabels.formatter = function (
        value: any
      ) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(value); // Display the raw value.
      };

      chart.config._config.options.scales.y.ticks = {
        callback: function (value: any) {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(value);
        },
      };
      if (data.type === GraphType.VerticalBar || data.type === GraphType.Bar) {
        chart.config._config.options.plugins.tooltip.callbacks.label =
          function (context: any) {
            let label = context.dataset.label || '';

            if (label) {
              label += ': ';
            }
            let val =
              context.dataset.axis === 'Y'
                ? context.parsed.x
                : context.parsed.y;
            label += new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(val);
            return label;
          };
      }

      if (data.type === GraphType.HorizontalBar) {
        chart.config._config.data.datasets.forEach((x: any) => {
          x.axis = 'Y';
        });

        chart.config._config.options.indexAxis = 'y';
        (chart.config._config.options.scales.y.ticks = {
          callback: function (value: any, index: number) {
            return chart.data.labels[index];
          },
          color: 'black',
          display: true,
        }),
          (chart.config._config.options.scales.y.grid = {
            display: false,
          });
      }
    } else {
      chart.config._config.options.plugins.tooltip.callbacks.label = function (
        context: any
      ) {
        let label = context.dataset.label || '';
        if (label) {
          label += ': ';
        }

        let val =
          data.type === GraphType.HorizontalBar
            ? context.parsed.x
            : context.parsed.y;
        label += val;
        return label;
      };
    }

    if (data.type === GraphType.HorizontalBar) {
      chart.config._config.data.datasets.forEach((c: any) => {
        c.axis = 'Y';
      });
      //chart.config._config.data.datasets.axis = 'Y';
      chart.config._config.data.labels = Object.values(chart.data.labels);
      if (this.isGraphHaveAmount(data.id)) {
        chart.config._config.options.scales.x.ticks = {
          callback: function (value: any) {
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(value);
          },
        };
        chart.config._config.options.plugins.tooltip.callbacks.label =
          function (context: any) {
            let label = context.dataset.label || '';

            if (label) {
              label += ': ';
            }
            let val =
              context.dataset.axis === 'Y'
                ? context.parsed.x
                : context.parsed.y;
            label += new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(val);
            return label;
          };
      }
    }

    let minMaxArray = _.flatten(
      data.series?.map((d: any) => {
        return [d.value, d.value1, d.value2];
      })
    );

    // Add values from previousYearSeries if they exist
    if (data.previousYearSeries && data.previousYearSeries.length > 0) {
      const prevValues = _.flatten(
        data.previousYearSeries.map((d: any) => [d.value, d.value1, d.value2])
      );
      minMaxArray = [...minMaxArray, ...prevValues];
    }
 
    if (data.type != GraphType.HorizontalBar) {
      let maxY = minMaxArray.reduce((a, b) => Math.max(a, b));
      let minY = minMaxArray.reduce((a, b) => Math.min(a, b));
        if (maxY <= 0) {
            maxY = Math.abs(minY) * 0.1; // 20% of the range as positive headroom
        }
      chart.config._config.options.plugins.datalabels.display = true;
      (chart.config._config.options.scales.y.min =
        this._utilService.roundToNext(minY)),
        (chart.config._config.options.scales.y.max =
          this._utilService.roundToNext(maxY * 1.1)), 
        chart.config._config.options.scales.y.grid = {
          display: true,
          color: 'rgba(235, 238, 243, 0.7)',
          drawBorder: false,
        };
        chart.config._config.options.scales.x.grid = {
          display: false,
          color: 'rgba(235, 238, 243, 0.7)',
          drawBorder: false,
        };
    }

    chart.config._config.options.layout = {
      padding: {
        top: 50,
        right: 50,
      },
    };

    if (data.id == Graphs.ARBreakdown) {
      let minMaxArray = data.series?.map((d: any) => {
        return (
          (d.value > 0 ? d.value : 0) +
          (d.value1 > 0 ? d.value1 : 0) +
          (d.value2 > 0 ? d.value2 : 0)
        );
      });
      let minMaxArray2 = data.series?.map((d: any) => {
        const v = d.value < 0 ? d.value : 0;
        const v1 = d.value1 < 0 ? d.value1 : 0;
        const v2 = d.value2 < 0 ? d.value2 : 0;
        return v + v1 + v2; // sum of negatives (or zero)
      });
      let maxY = minMaxArray.reduce((a, b) => Math.max(a, b));
      let minY = minMaxArray2.reduce((a, b) => Math.min(a, b));
      if (maxY <= 0) {
        maxY = Math.abs(minY) * 0.1; // 20% of the range as positive headroom
      }
      chart.config._config.options.scales.y.min =
        this._utilService.roundToNext(minY);
      chart.config._config.options.scales.y.max =
        this._utilService.roundToNext(maxY);
      chart.config._config.options.plugins.datalabels.display = true;
      chart.config._config.options.scales.y.min =
        this._utilService.roundToNext(minY);
      chart.config._config.options.scales.y.max =
        this._utilService.roundToNext(maxY);
      chart.config._config.options.scales.y.grid = {
        display: false,
      };
      chart.config._config.options.scales.x.grid = {
        display: false,
      };
      chart.config._config.options.layout = {
        padding: {
          top: 50,
          right: 50,
        },
      };
      chart.config._config.data.datasets.forEach((c: any) => {
        c.axis = 'X';
      });
      chart.config._config.options.plugins.tooltip.callbacks.label = function (
        context: any
      ) {
        let label = context.dataset.label || '';

        if (label) {
          label += ': ';
        }
        let val =
          context.dataset.axis === 'Y' ? context.parsed.x : context.parsed.y;
        label += new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(val);
        return label;
      };
    }
    // Attach callbacks after comparison options have been replaced.
    this.applyComparisonLegendCursors(chart, compare);
    return chart;
  }

  private applyComparisonLegendCursors(chart: any, compare?: boolean): void {
    if (!compare) return;

    // Read the replaced options before chart.update() refreshes chart.options.
    const legend = chart.config.options?.plugins?.legend;
    if (!legend || legend.display === false) return;

    let previousCursor: string | undefined;
    legend.onHover = (_event: any, _item: any, activeLegend: any) => {
      // Use resolved options so Chart.js defaults and explicitly disabled clicks are respected.
      if (typeof activeLegend.options.onClick !== 'function') return;
      previousCursor ??= chart.canvas.style.cursor;
      chart.canvas.style.cursor = 'pointer';
    };
    legend.onLeave = () => {
      if (previousCursor !== undefined) {
        chart.canvas.style.cursor = previousCursor;
        previousCursor = undefined;
      }
    };
  }

  lineChart(data: ChartResp, type: number) {
    let model = this.getChartModel(data);
    let datasource = JSON.parse(JSON.stringify(this.lineData));
    let options = JSON.parse(JSON.stringify(this.lineOptions));
    datasource.labels = model.labels;
    //Set Data
    datasource.datasets[0].data = model.data;

    if (model.xLabel != '') {
      options.scales.x.title.text = model.xLabel;
    }
    if (model.yLabel != '') {
      options.scales.y.title.text = model.yLabel;
    }

    if (type == Graphs.TotalAR) {
      datasource.datasets[0].borderColor = ['#E96E29'];
      datasource.datasets[0].backgroundColor = ['#E96E29'];
    }
    if (type == Graphs.NewPatientsAdded) {
      datasource.datasets[0].borderColor = ['#526BE8'];
      datasource.datasets[0].backgroundColor = ['#526BE8'];
    }
    if (type == Graphs.CompletedClaims) {
      datasource.datasets[0].borderColor = ['#369a25'];
      datasource.datasets[0].backgroundColor = ['#369a25'];
    }
    if (type == Graphs.CompletedAppointment) {
      datasource.datasets[0].borderColor = ['#F7B42E'];
      datasource.datasets[0].backgroundColor = ['#F7B42E'];
    }

    options.plugins.tooltip = {
      backgroundColor: '#fff',
      bodyColor: '#000',
      borderColor: '#cdcdcd',
      borderWidth: 1,
      cornerRadius: 4,
      titleColor: '#000',
      callbacks: {
        label: '',
      },
    };
    if (this.isGraphHaveAmount(type)) {
      options.plugins.datalabels.formatter = function (value: any) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(value); // Display the raw value.
      };

      (options.scales.y.ticks = {
        callback: function (value: any) {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(value);
        },
      }),
        // }
        (options.plugins.tooltip.callbacks = {
          label: '',
        });

      options.plugins.tooltip.callbacks.label = function (context: any) {
        let label = context.dataset.label || '';

        if (label) {
          label += ': ';
        }
        let val = context.parsed.y;
        // label += new Intl.NumberFormat('en-US', {
        //   style: 'currency',
        //   currency: 'USD',
        // }).format(val);
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(val);
      };
    } else {
      options.plugins.tooltip.callbacks.label = function (context: any) {
        let label = context.dataset.label || '';
        if (label) {
          label += ': ';
        }

        let val =
          context.dataset.axis === 'Y' ? context.parsed.x : context.parsed.y;
        label += val;
        return label;
      };
    }
    options.layout = {
      padding: {
        top: 50,
        right: 50,
      },
    };

    options.scales.x.offset = true;
    options.scales.y.beginAtZero = true;

    return {
      type: 'line',
      data: datasource,
      options: options,
    };
  }

  updateline(chart: any, data: ChartResp, compare?: boolean) {
    let model = this.getChartModel(data);
    let config = this.getChartModel(data);
    let datasource = JSON.parse(JSON.stringify(this.lineData));
    chart.data = chart.data ?? {};
    chart.data.datasets = chart.data.datasets ?? [{}];
    chart.data.datasets[0].data = model.data;
    chart.data.labels = model.labels;
    if (model.xLabel != '') {
      chart.options.scales.x.title.text = model.xLabel;
    }
    if (model.yLabel != '') {
      chart.options.scales.y.title.text = model.yLabel;
    }

    if (compare) {
      chart.config._config.data.labels = config.labels.splice(
        0,
        Number(config.labels?.length || 0) / 2
      );

      chart.config._config.data.datasets = [
        {
          label: data.seriesLabel, //data.xlabel,
          data: data.series.map((x) => x.value),
          borderColor: '#2491EB',
          backgroundColor: 'rgba(24, 139, 204, 0.1)',
          borderWidth: 2,
          fill: false,
          pointRadius: 4,
          pointBackgroundColor: '#FFFFFF',
          pointBorderColor: '#2491EB',
        },
        {
          label: data.previousYearSeriesLabel, //data.previousYearXlabel,
          data: data.previousYearSeries?.map((x) => x.value),
          borderColor: '#F1416C',
          backgroundColor: 'rgba(241, 65, 108, 0.1)',
          borderWidth: 2,
          fill: false,
          pointRadius: 4,
          pointBackgroundColor: '#FFFFFF',
          pointBorderColor: '#F1416C',
        },
      ];

      chart.config._config.options = {
        animation: {
          duration: 400,
          easing: 'easeOutQuart',
        },
        transitions: {
          active: {
            animation: {
              duration: 0,
            },
          },
        },
        layout: {
          padding: {
            top: 50,
            right: 50,
          },
        },
        responsive: true,
        plugins: {
          datalabels: {
            align: 'top',
            anchor: 'end',
            backgroundColor: '#fff',
            borderColor: '#DAE1E5',
            borderRadius: 4,
            borderWidth: 1,
            color: 'black',
            display: false,
            font: {
              size: '10',
            },
          },
          legend: {
            position: 'top',
            display: true,
            labels: {
              usePointStyle: false,
            },
          },
          tooltip: {
            backgroundColor: '#fff',
            bodyColor: '#000',
            borderColor: '#cdcdcd',
            borderWidth: 1,
            cornerRadius: 4,
            titleColor: '#000',
          },
        },
        scales: {
          x: {
            title: {
              display: true,
              text: model.xLabel,
              color: '#000',
              font: {
                size: 16,
                weight: 'bold',
              },
              padding: {
                top: 10,
                bottom: 5,
                left: 0,
                right: 0,
              },
            },
            grid: {
              display: false,
            },
          },
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: model.yLabel,
              color: '#000',
              font: {
                size: 16,
                weight: 'bold',
              },
              padding: {
                top: 10,
                bottom: 5,
                left: 0,
                right: 0,
              },
            },
            ticks: {
              color: 'black',
            },
            grid: {
              display: false,
            },
          },
        },
      };
    } else {
      chart.data.datasets = [
        {
          label: data.xlabel,
          data: data.series.map((x) => x.value),
          borderColor: ['#9AD0F5'],
          backgroundColor: ['#9AD0F5'],
          borderWidth: 2,
          fill: false,
          pointRadius: 4,
          pointBackgroundColor: ['#9AD0F5'],
        },
      ];
      let color = datasource.datasets[0].borderColor;
      if (data.id == Graphs.TotalAR) {
        color = '#E96E29';
      }
      if (data.id == Graphs.NewPatientsAdded) {
        color = '#526BE8';
      }
      if (data.id == Graphs.CompletedClaims) {
        color = '#369a25';
      }
      if (data.id == Graphs.CompletedAppointment) {
        color = '#F7B42E';
      }

      chart.data.datasets[0].borderColor = [color];
      chart.data.datasets[0].backgroundColor = [color];
      chart.data.datasets[0].pointBackgroundColor = [color];
      chart.options = {
        animation: {
          duration: 400,
          easing: 'easeOutQuart',
        },
        transitions: {
          active: {
            animation: {
              duration: 0,
            },
          },
        },
        layout: {
          padding: {
            top: 50,
            right: 50,
          },
        },
        indexAxis: '',
        plugins: {
          datalabels: {
            align: 'top',
            anchor: 'end',
            backgroundColor: '#fff',
            borderColor: '#DAE1E5',
            borderRadius: 4,
            borderWidth: 1,
            color: 'black',
            display: false,
            font: {
              size: '10',
            },
          },
          legend: {
            display: false,
            labels: {
              usePointStyle: false,
            },
          },
          tooltip: {
            backgroundColor: '#fff',
            bodyColor: '#000',
            borderColor: '#cdcdcd',
            borderWidth: 1,
            cornerRadius: 4,
            titleColor: '#000',
          },
        },
        responsive: true,
        scales: {
          x: {
            axis: 'x',
            beginAtZero: true,
            border: {
              color: 'rgba(0,0,0,0.1)',
              dash: [],
              dashOffset: 0,
              display: true,
              width: 1,
            },
            bounds: 'ticks',
            clip: true,
            display: true,
            grace: 0,
            grid: {
              color: 'rgba(0,0,0,0.1)',
              display: false,
              drawOnChartArea: true,
              drawTicks: true,
              lineWidth: 1,
              offset: true,
              tickLength: 8,
            },
            id: 'x',
            offset: true,
            position: 'bottom',
            reverse: false,
            ticks: {
              align: 'center',
              autoSkip: true,
              autoSkipPadding: 3,
              backdropColor: 'rgba(255, 255, 255, 0.75)',
              backdropPadding: 2,
              color: 'black',
              crossAlign: 'near',
              display: true,
              labelOffset: 0,
              major: {},
              maxRotation: 50,
              minor: {},
              minRotation: 0,
              mirror: false,
              padding: 3,
              showLabelBackdrop: false,
              textStrokeColor: '',
              textStrokeWidth: 0,
            },
            title: {
              align: 'center',
              color: 'black',
              display: true,
              font: {
                size: 16,
                weight: 'bold',
              },
              padding: {
                bottom: 5,
                left: 0,
                right: 0,
                top: 10,
              },
              text: data.xlabel,
              ticks: {},
            },
            type: 'category',
          },
          y: {
            axis: 'y',
            beginAtZero: true,
            border: {
              color: 'rgba(0,0,0,0.1)',
              dash: [],
              dashOffset: 0,
              display: true,
              width: 1,
            },
            bounds: 'ticks',
            clip: true,
            display: true,
            grace: 0,
            grid: {
              color: 'rgba(0,0,0,0.1)',
              display: false,
              drawOnChartArea: true,
              drawTicks: true,
              lineWidth: 1,
              offset: false,
              tickLength: 8,
            },
            id: 'y',
            offset: false,
            position: 'left',
            reverse: false,
            ticks: {
              align: 'center',
              autoSkip: true,
              autoSkipPadding: 3,
              backdropColor: 'rgba(255, 255, 255, 0.75)',
              backdropPadding: 2,
              color: 'black',
              crossAlign: 'near',
              display: true,
              labelOffset: 0,
              major: {},
              maxRotation: 50,
              minor: {},
              minRotation: 0,
              mirror: false,
              padding: 3,
              showLabelBackdrop: false,
              textStrokeColor: '',
              textStrokeWidth: 0,
            },
            title: {
              align: 'center',
              color: 'black',
              display: true,
              font: {
                size: 16,
                weight: 'bold',
              },
              padding: {
                bottom: 5,
                left: 0,
                right: 0,
                top: 10,
              },
              text: 'Amount',
            },
            type: 'linear',
          },
        },
      };
    }

    if (this.isGraphHaveAmount(data.id)) {
      chart.config._config.options.plugins.datalabels.formatter = function (
        value: any
      ) {
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(value); // Display the raw value.
      };

      (chart.config._config.options.scales.y.ticks = {
        callback: function (value: any) {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(value);
        },
      }),
        // }
        (chart.config._config.options.plugins.tooltip.callbacks = {
          label: '',
        });

      chart.config._config.options.plugins.tooltip.callbacks.label = function (
        context: any
      ) {
        let label = context.dataset.label || '';

        if (label) {
          label += ': ';
        }
        let val =
          context.dataset.axis === 'Y' ? context.parsed.x : context.parsed.y;
        label += new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(val);
        return data.series && data.previousYearSeries
          ? label
          : new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(val);
      };
    } else {
      chart.config._config.options.plugins.tooltip.callbacks = { label: '' };
      chart.config._config.options.plugins.tooltip.callbacks.label = function (
        context: any
      ) {
        let label = context.dataset.label || '';
        if (label) {
          label += ': ';
        }

        let val =
          context.dataset.axis === 'Y' ? context.parsed.x : context.parsed.y;
        label += val;
        return data.series && data.previousYearSeries ? label : val;
      };
    }
    chart.config._config.options.scales.x.offset = true;

     let minMaxArray = _.flatten(
      data.series?.map((d: any) => {
        return [d.value, d.value1, d.value2];
      })
    );

    // Add values from previousYearSeries if they exist
    if (data.previousYearSeries && data.previousYearSeries.length > 0) {
      const prevValues = _.flatten(
        data.previousYearSeries.map((d: any) => [d.value, d.value1, d.value2])
      );
      minMaxArray = [...minMaxArray, ...prevValues];
    }
 
    let maxY = minMaxArray.reduce((a, b) => Math.max(a, b));
    let minY = minMaxArray.reduce((a, b) => Math.min(a, b));
    if (maxY <= 0) {
        maxY = Math.abs(minY) * 0.1; // 20% of the range as positive headroom
    }
    chart.config._config.options.plugins.datalabels.display = false;
    (chart.config._config.options.scales.y.min =
      this._utilService.roundToNext(minY)),
      (chart.config._config.options.scales.y.max =
        this._utilService.roundToNext(maxY * 1.1)),
      (chart.config._config.options.scales.y.grid = {
        display: false,
      }),
      (chart.config._config.options.scales.x.grid = {
        display: false,
      });
    chart.config._config.options.layout = {
      padding: {
        top: 50,
        right: 50,
      },
    };

    chart.config._config.options.plugins.tooltip = {
      ...chart.config._config.options.plugins.tooltip,
      usePointStyle: true,
      pointStyle: 'circle',
      backgroundColor: '#1F2937',
      bodyColor: '#FFFFFF',
      borderColor: 'rgba(255, 255, 255, 0.15)',
      borderWidth: 1,
      callbacks: {},
      cornerRadius: 8,
      titleColor: '#FFFFFF',
    }

    // Attach callbacks after comparison options have been replaced.
    this.applyComparisonLegendCursors(chart, compare);
    return chart;
  }

  doughnutChart(data: ChartResp, type: number, id: number = 0) {
    let model = this.getChartModel(data);
    let datasource = JSON.parse(JSON.stringify(this.doughnutData));
    let options = JSON.parse(JSON.stringify(this.doughnutOptions));

    options.plugins.legend.onHover = function (event: any) {
      event.native.target.style.cursor = 'pointer';
    };
    options.plugins.legend.onLeave = function (event: any) {
      event.native.target.style.cursor = 'default';
    };
    datasource.labels = model.labels;
    datasource.datasets[0].data = model.data;

    // Set the chart type based on the `type`
    let gtype = type === GraphType.Pie ? 'pie' : 'doughnut';

    // Handle currency formatting if applicable
    if (this.isGraphHaveAmount(id)) {
      options.plugins.tooltip.callbacks.label = function (context: any) {
        let label = context.dataset.label || '';
        if (label) {
          label += ': ';
        }
        label += new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(context.parsed);
        return label;
      };
    } else {
      options.plugins.tooltip.callbacks.label = function (context: any) {
        let label = context.dataset.label || '';
        if (label) {
          label += ': ';
        }
        let val = context.parsed;
        label += val;
        return label;
      };
    }

    // If the chart is for CCPaymentTrends, adjust specific options
    if (
      id === Graphs.CCPaymentTrends ||
      id === Graphs.CampaignByStatus ||
      id === Graphs.CollectionByMethod
    ) {
      // Optionally set the cutout (uncomment if needed)
      // datasource.datasets[0].cutout = 60;  // Adjust the inner radius (cutout) if required
      options.responsive = false; // Disable responsiveness
    }

    if (id === Graphs.CampaignByStatus || id === Graphs.CollectionByMethod) {
      options.plugins.legend.labels.generateLabels = function (chart: any) {
        return chart.data.labels.map(function (label: any, i: any) {
          const meta = chart.getDatasetMeta(0);
          const style = meta.controller.getStyle(i);
          return {
            text:
              label +
              ': ' +
              new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
              }).format(chart.data.datasets[0].data[i]),
            fillStyle: chart.data.datasets[0].backgroundColor[i],
            strokeStyle: style.borderColor,
            lineWidth: style.borderWidth,
            hidden: !chart.getDataVisibility(i),
            index: i,
          };
        });
      };

      options.plugins.tooltip.callbacks.label = function (context: any) {
        let label = context.dataset.label || '';
        if (label) {
          label += ': ';
        }
        label += new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(context.parsed);
        return label;
      };
    }

    return {
      type: gtype,
      data: datasource,
      options: options,
    };
  }

  updateDoughnut(chart: any, data: ChartResp) {
    let model = this.getChartModel(data);
    let datasource = JSON.parse(JSON.stringify(this.doughnutData));
    let options = JSON.parse(JSON.stringify(this.doughnutOptions));
    // chart.data.datasets[0].data = model.data;
    // chart.data.labels = model.labels;
    datasource.labels = model.labels;
    datasource.datasets[0].data = model.data;

    // Set the chart type based on the `type`
    let gtype = data.type === GraphType.Pie ? 'pie' : 'doughnut';

    // Handle currency formatting if applicable
    if (this.isGraphHaveAmount(data.id)) {
      options.plugins.tooltip.callbacks.label = function (context: any) {
        let label = context.dataset.label || '';
        if (label) {
          label += ': ';
        }
        label += new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(context.parsed);
        return label;
      };
    } else {
      options.plugins.tooltip.callbacks.label = function (context: any) {
        let label = context.dataset.label || '';
        if (label) {
          label += ': ';
        }
        let val = context.parsed;
        label += val;
        return label;
      };
    }

    // If the chart is for CCPaymentTrends, adjust specific options
    if (
      data.id === Graphs.CCPaymentTrends ||
      data.id === Graphs.CampaignByStatus ||
      data.id === Graphs.CollectionByMethod
    ) {
      // Optionally set the cutout (uncomment if needed)
      // datasource.datasets[0].cutout = 60;  // Adjust the inner radius (cutout) if required
      options.responsive = false; // Disable responsiveness
    }

    if (data.id === Graphs.CampaignByStatus || data.id === Graphs.CollectionByMethod) {
      options.plugins.legend.labels.generateLabels = function (chart: any) {
        return chart.data.labels.map(function (label: any, i: any) {
          return {
            text:
              label +
              ': ' +
              new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
              }).format(chart.data.datasets[0].data[i]),
            fillStyle: chart.data.datasets[0].backgroundColor[i],
          };
        });
      };

      options.plugins.tooltip.callbacks.label = function (context: any) {
        let label = context.dataset.label || '';
        if (label) {
          label += ': ';
        }
        label += new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(context.parsed);
        return label;
      };
    }

    options.devicePixelRatio = typeof window !== 'undefined' ? (window.devicePixelRatio * 2) : 4;

    return {
      type: gtype,
      data: datasource,
      options: options,
    };
  }

  // Helper function to generate random colors
  getRandomColor(index: number = 0): string {
    if (index == 1) {
      return '#369A25';
    } else if (index == 2) {
      return '#F7B42E';
    } else if (index == 3) {
      return '#526BE8';
    } else if (index == 4) {
      return '#7A3CDE';
    } else if (index == 5) {
      return '#1EC3DB';
    } else if (index == 6) {
      return '#F03C9D';
    } else {
      const letters = '0123456789ABCDEF';
      let color = '#';
      for (let i = 0; i < 6; i++) {
        color += letters[Math.floor(Math.random() * 16)];
      }
      return color;
    }
  }

  selectColorBasedOnKey(key: string): string {
    switch (key) {
      case this.paymentMethods.SAVED_CARD:
        return '#526BE8';
      case this.paymentMethods.VIRTUAL_TERMINAL:
        return '#FF5733';
      case this.paymentMethods.PHYSICAL_TERMINAL:
        return '#FFC300';
      // case this.paymentMethods.CASH:
      //   return '#28A745';
      // case this.paymentMethods.CHECK:
      //   return '#6F42C1';
      case this.paymentMethods.ACH_OR_E_CHECK:
        return '#17A2B8';
      case this.paymentMethods.PAYMENT_LINK:
        return '#FF851B';
      default:
        return '#DC3545';
    }
  }

  stackedChart(data: any, id: number = 0, xLabel: string = '') {
    let datasource = JSON.parse(JSON.stringify(this.bartData));
    let options = JSON.parse(JSON.stringify(this.barOptions));

    const isHorizontal = id === Graphs.OverallTrends;

    if (isHorizontal) {
      options.scales.x.title.text = 'Amount';
      options.scales.y.title.text = '';
      options.responsive = false;
      options.indexAxis = 'y';
    } else {
      options.scales.y.title.text = 'Amount';
      options.scales.x.title.text = xLabel;
      options.responsive = true;
      options.indexAxis = 'x';
    }

    datasource.labels = [];

    // Set to store unique keys
    const uniqueKeys: Set<string> = new Set();

    // Collect all unique keys
    data.forEach((week: any) => {
      datasource.labels.push(week.key);
      week.values = week.values?.sort((a: any, b: any) => a.value - b.value);
      week.values?.forEach((value: any) => {
        uniqueKeys.add(value.key);
      });
    });
    // Convert the set to an array
    const uniqueKeysList: string[] = Array.from(uniqueKeys);
    // Create the final data structure for the stacked graph
    let stackedData = uniqueKeysList.map((key, index) => {
      // Collect the values for this key across all weeks
      const dataForKey = data.map((week: any) => {
        week.values = week.values?.sort((a: any, b: any) => a.value - b.value);
        const valueForKey = week.values?.find((value: any) => value.key === key);
        return valueForKey ? valueForKey.value : 0;
      });

      return {
        label: key,
        data: dataForKey,
        backgroundColor:
          !isHorizontal
            ? this.getRandomColor(index + 1)
            : this.selectColorBasedOnKey(key), // Function to assign color to each series
        borderRadius: 8,
        axis: isHorizontal ? 'Y' : 'X',
        // maxBarThickness: 20,
      };
    });
    stackedData = stackedData?.sort((a: any, b: any) => {
      return Number(a.data?.[0] || 0) - Number(b.data?.[0] || 0);
    });
    datasource.datasets = stackedData;
    options.scales.x.stacked = true;
    options.scales.y.stacked = true;
    options.plugins.datalabels.display = false;

    if (!isHorizontal) {
      options.scales.x.offset = true;
      options.scales.x.grid = {
        display: false,
        offset: true,
      };
      options.scales.x.ticks = {
        ...options.scales.x.ticks,
        align: 'center',
      };
      options.scales.y.beginAtZero = true;
    }

    options.plugins.legend.display = true;
    options.plugins.legend.position = 'top';
    options.plugins.legend.labels.usePointStyle = true;

    options.layout = {
      padding: {
        top: 50,
        right: 30,
        bottom: 10,
        left: 10,
      },
    };

    options.plugins = {
      datalabels: {
        display: false, // This ensures labels are always displayed
        anchor: 'center', // Where the label is placed relative to the data point.
        align: 'center', // Alignment of the label.
        backgroundColor: '#fff',
        borderColor: '#DAE1E5',
        borderWidth: 1,
        borderRadius: 4,
        color: 'black',
        font: {
          size: '10',
        },
        formatter: function (value: any) {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(value); // Display the raw value.
        },
      },
      legend: {
        position: 'top',
        display: true,
        labels: {
          usePointStyle: true,
          color: 'black',
          padding: 20,
        },
      },
      tooltip: {
        backgroundColor: '#1F2937',
        titleColor: '#FFFFFF',
        bodyColor: '#FFFFFF',
        borderColor: 'rgba(255, 255, 255, 0.15)',
        borderWidth: 1,
        cornerRadius: 8,
        callbacks: {
          label: function (context: any) {
            let label = context.dataset.label || '';

            if (label) {
              label += ': ';
            }
            let val = 0;
            if (context.dataset.axis == 'Y') {
              val = context.parsed.x;
            }
            if (context.dataset.axis == 'X') {
              val = context.parsed.y;
            }

            label += new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
            }).format(val);
            return label;
          },
        },
      },
    };

    if (id === Graphs.OverallTrends) {
      options.plugins.tooltip.callbacks = { label: '' };
      options.plugins.tooltip.callbacks.label = function (context: any) {
        let label = context.dataset.label || '';
        if (label) {
          label += ': ';
        }
        let val = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(context.parsed.x);
        label += val;
        return label;
      };
      options.responsive = true;
      options.scales.x.title = {
        align: 'center',
        color: 'black',
        display: true,
        font: {
          size: 16,
          weight: 'bold',
        },
        padding: {
          bottom: 5,
          left: 0,
          right: 0,
          top: 10,
        },
        text: 'Amount',
      };
      options.scales.x.ticks = {
        callback: function (value: any) {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(value);
        },
      };
    }

    const axis = isHorizontal ? 'y' : 'x'

    options.scales[axis].ticks.callback = function (value: any) {
      return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
      }).format(value);
    };

    if (options?.plugins?.legend && options.plugins.legend.display !== false) {
      options.plugins.legend.onHover = function (event: any) {
        if (event?.native?.target) {
          event.native.target.style.cursor = 'pointer';
        }
      };

      options.plugins.legend.onLeave = function (event: any) {
        if (event?.native?.target) {
          event.native.target.style.cursor = 'default';
        }
      };
    }

    return {
      type: 'bar',
      data: datasource,
      options: options,
    };
  }

  updateStacked(
    chart: any,
    data: any,
    id: number = 0,
    xlabel: string = '',
    previousYearXlabel?: any,
    previousYearSeries?: any,
    compare?: boolean
  ) {
    chart.data = chart.data ?? {};
    chart.data.datasets = [];
    chart.data.labels = [];

    const isHorizontal = id === Graphs.OverallTrends;

    if (compare) {
      const uniqueKeys: Set<string> = new Set();
      // Collect all unique keys
      data.forEach((week: any) => {
        chart.data.labels.push(week.key);
        week.values = week.values?.sort((a: any, b: any) => a.value - b.value);
        week.values?.forEach((value: any) => {
          uniqueKeys.add(value.key);
        });
      });
      // Convert the set to an array
      const uniqueKeysList: string[] = Array.from(uniqueKeys);
      // Create the final data structure for the stacked graph
      const stackedData = uniqueKeysList.map((key, index) => {
        // Collect the values for this key across all weeks
        const dataForKey = data.map((week: any) => {
          const valueForKey = week.values.find(
            (value: any) => value.key === key
          );
          return valueForKey ? valueForKey.value : 0;
        });

        return {
          label: `${key} ( ${xlabel} )`,
          data: dataForKey,
          backgroundColor: this.getRandomColor(index + 1),
          borderRadius: 8,
          // maxBarThickness: 20,
          stack: xlabel,
          axis: isHorizontal ? 'Y' : 'X',
        };
      });
      if (previousYearSeries && previousYearSeries?.length) {
        const uniqueKeysPrev: Set<string> = new Set();
        // Collect all unique keys
        previousYearSeries.forEach((week: any) => {
          chart.data.labels.push(week.key);
          week.values = week.values?.sort(
            (a: any, b: any) => a.value - b.value
          );
          week.values?.forEach((value: any) => {
            uniqueKeys.add(value.key);
          });
        });
        // Convert the set to an array
        const uniqueKeysListPrev: string[] = Array.from(uniqueKeysPrev);

        const prevStackedData = uniqueKeysListPrev.map((key, index) => {
          // Collect the values for this key across all weeks
          const dataForKeyPrev = previousYearSeries?.map((week: any) => {
            const valueForKeyPrev = week.values.find(
              (value: any) => value.key === key
            );
            return valueForKeyPrev ? valueForKeyPrev.value : 0;
          });

          return {
            label: `${key} ( ${previousYearXlabel} )`,
            data: dataForKeyPrev,
            backgroundColor: this.getRandomColor(index + 1),
            borderRadius: 8,
            axis: isHorizontal ? 'Y' : 'X',
            // maxBarThickness: 20,
            stack: previousYearXlabel,
          };
        });
        chart.data.labels = chart.data.labels?.splice(
          Number(Number(chart.data.labels?.length || 0) / 2)
        );
        chart.options = {
          animation: {
            duration: 1000,
            easing: 'easeOutQuart',
          },
          indexAxis: isHorizontal ? 'y' : 'x',
          layout: {
            padding: {
              top: 50,
              right: 30,
              bottom: 10,
              left: 10,
            },
          },
          plugins: {
            datalabels: {
              display: false,
              anchor: 'center', // Where the label is placed relative to the data point.
              align: 'center', // Alignment of the label.
              backgroundColor: '#fff',
              borderColor: '#DAE1E5',
              borderWidth: 1,
              borderRadius: 4,
              color: 'black',
              font: {
                size: '10',
              },
              formatter: function (value: any) {
                return new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                }).format(value); // Display the raw value.
              },
            },
            legend: {
              position: 'top',
              display: true,
              labels: {
                usePointStyle: true,
                color: 'black',
                padding: 20,
              },
            },
            tooltip: {
              backgroundColor: '#1F2937',
              titleColor: '#FFFFFF',
              bodyColor: '#FFFFFF',
              borderColor: 'rgba(255, 255, 255, 0.15)',
              borderWidth: 1,
              cornerRadius: 8,
            },
          },
          interaction: {
            intersect: false,
          },
          scales: {
            x: {
              stacked: true,
              offset: !isHorizontal,
              grid: {
                display: false,
                offset: !isHorizontal,
              },
              ticks: {
                align: 'center',
              },
            },
            y: {
              stacked: true,
              beginAtZero: true,
              grid: {
                display: false,
              },
              ticks: {
                callback: function (value: any) {
                  return new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                  }).format(value);
                },
              },
            },
          },
        };
        chart.data.datasets = [...stackedData, ...prevStackedData];
      } else {
        chart.data.datasets = stackedData;
      }
    } else {
      const uniqueKeys: Set<string> = new Set();
      // Collect all unique keys
      data.forEach((week: any) => {
        chart.data.labels.push(week.key);
        week.values = week.values?.sort((a: any, b: any) => b.value - a.value);
        week.values?.forEach((value: any) => {
          uniqueKeys.add(value.key);
        });
      });
      // Convert the set to an array
      const uniqueKeysList: string[] = Array.from(uniqueKeys);
      // Create the final data structure for the stacked graph
      const stackedData = uniqueKeysList.map((key, index) => {
        const reversedIndex = uniqueKeysList.length - 1 - index;
        // Collect the values for this key across all weeks
        const dataForKey = data.map((week: any) => {
          week.values = week.values?.sort(
            (a: any, b: any) => a.value - b.value
          );
          const valueForKey = week.values?.find(
            (value: any) => value.key === key
          );
          return valueForKey ? valueForKey.value : 0;
        });

        return {
          label: key,
          data: dataForKey,
          backgroundColor:
            !isHorizontal
              ? this.getRandomColor(index + 1)
              : this.selectColorBasedOnKey(key), // Function to assign color to each series
          borderRadius: 8,
          axis: isHorizontal ? 'Y' : 'X',
          // maxBarThickness: 20,
        };
      });
      chart.data.datasets = stackedData;
      chart.options = {
        animation: {
          duration: 1000,
          easing: 'easeOutQuart',
        },
        indexAxis: isHorizontal ? 'y' : 'x',
        layout: {
          padding: {
            top: 50,
            right: 30,
            bottom: 10,
            left: 10,
          },
        },
        plugins: {
          datalabels: {
            display: false,
            anchor: 'center', // Where the label is placed relative to the data point.
            align: 'center', // Alignment of the label.
            backgroundColor: '#fff',
            borderColor: '#DAE1E5',
            borderWidth: 1,
            borderRadius: 4,
            color: 'black',
            font: {
              size: '10',
            },
            formatter: function (value: any) {
              return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
              }).format(value); // Display the raw value.
            },
          },
          legend: {
            position: 'top',
            display: true,
            labels: {
              usePointStyle: true,
              color: 'black',
              padding: 20,
            },
          },
          tooltip: {
            backgroundColor: '#1F2937',
            titleColor: '#FFFFFF',
            bodyColor: '#FFFFFF',
            borderColor: 'rgba(255, 255, 255, 0.15)',
            borderWidth: 1,
            cornerRadius: 8,
            callbacks: {
              label: function (context: any) {
                let label = context.dataset.label || '';

                if (label) {
                  label += ': ';
                }
                let val = 0;
                if (context.dataset.axis == 'Y') {
                  val = context.parsed.x;
                }
                if (context.dataset.axis == 'X') {
                  val = context.parsed.y;
                }

                label += new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                }).format(val);
                return label;
              },
            },
          },
        },
        interaction: {
          intersect: false,
        },
        scales: {
          x: {
            stacked: true,
            offset: !isHorizontal,
            grid: {
              display: false,
              offset: !isHorizontal,
            },
            ticks: {
              align: 'center',
            },
          },
          y: {
            stacked: true,
            beginAtZero: true,
            grid: {
              display: false,
            },
            ticks: {
              callback: function (value: any) {
                return new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                }).format(value);
              },
            },
          },
        },
      };
    }

    chart.data.datasets = chart.data.datasets.map((x: any) => {
      x.axis = isHorizontal ? 'Y' : 'X';
      return x;
    });

    const applyDetailedOptions = (opts: any) => {
      if (!opts) return;
      opts.responsive = isHorizontal ? false : true;
      opts.indexAxis = isHorizontal ? 'y' : 'x';

      if (opts.interaction) {
        delete opts.interaction;
      }

      if (!opts.layout) opts.layout = {};
      opts.layout.padding = {
        top: 50,
        right: 30,
        bottom: 10,
        left: 10,
      };

      if (!opts.plugins) opts.plugins = {};
      if (opts?.plugins?.legend && opts.plugins.legend.display !== false) {
        opts.plugins.legend.onHover = function (event: any) {
          if (event?.native?.target) {
            event.native.target.style.cursor = 'pointer';
          }
        };

        opts.plugins.legend.onLeave = function (event: any) {
          if (event?.native?.target) {
            event.native.target.style.cursor = 'default';
          }
        };
      }
      if (!opts.plugins.tooltip) opts.plugins.tooltip = {};
      opts.plugins.tooltip.callbacks = { label: '' };
      opts.plugins.tooltip.callbacks.label = function (context: any) {
        let label = context.dataset.label || '';
        if (label) {
          label += ': ';
        }
        let val = new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
        }).format(
          isHorizontal ? context.parsed.x : context.parsed.y
        );
        label += val;
        return label;
      };

      if (!opts.scales) opts.scales = {};
      if (!opts.scales.x) opts.scales.x = {};
      if (!opts.scales.y) opts.scales.y = {};

      opts.scales.x.stacked = true;
      opts.scales.y.stacked = true;
      opts.scales.y.beginAtZero = true;

      opts.scales.x.offset = !isHorizontal;
      if (!opts.scales.x.grid) opts.scales.x.grid = {};
      opts.scales.x.grid.offset = !isHorizontal;
      opts.scales.x.grid.display = false;

      if (!opts.scales.x.ticks) opts.scales.x.ticks = {};
      opts.scales.x.ticks.align = 'center';

      opts.scales.x.title = {
        align: 'center',
        color: 'black',
        display: true,
        font: {
          size: 16,
          weight: 'bold',
        },
        padding: {
          bottom: 5,
          left: 0,
          right: 0,
          top: 10,
        },
        text: isHorizontal ? 'Amount' : xlabel,
      };

      opts.scales.y.title = {
        align: 'center',
        color: 'black',
        display: true,
        font: {
          size: 16,
          weight: 'bold',
        },
        padding: {
          bottom: 5,
          left: 0,
          right: 0,
          top: 10,
        },
        text: isHorizontal ? xlabel : 'Amount',
      };

      if (isHorizontal) {
        opts.scales.x.ticks.callback = function (value: any) {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(value);
        };
        opts.scales.y.ticks.callback = function (value: any, index: number) {
          return typeof this.getLabelForValue === 'function'
            ? this.getLabelForValue(value)
            : (chart.data.labels?.[index] ?? value);
        };
      } else {
        opts.scales.y.ticks.callback = function (value: any) {
          return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
          }).format(value);
        };
        opts.scales.x.ticks.callback = function (value: any, index: number) {
          return typeof this.getLabelForValue === 'function'
            ? this.getLabelForValue(value)
            : (chart.data.labels?.[index] ?? value);
        };
      }
    };

    applyDetailedOptions(chart.options);
    if (chart.config?._config?.options) {
      applyDetailedOptions(chart.config._config.options);
    }
    if (chart.config?.options && chart.config.options !== chart.options) {
      applyDetailedOptions(chart.config.options);
    }

    chart = this._utilService.sortDatasetsByTotal(chart);
    if (chart.config?._config?.options) {
      chart.config._config.options.devicePixelRatio = typeof window !== 'undefined' ? (window.devicePixelRatio * 2) : 4;
    }
    if (chart.options) {
      chart.options.devicePixelRatio = typeof window !== 'undefined' ? (window.devicePixelRatio * 2) : 4;
    }
    return chart;
  }

  getChartModel(data: ChartResp) {
    let model = {
      goal: data.goal,
      xLabel: data.xlabel,
      yLabel: data.ylabel,
      labels: [],
      data: [],
      data1: [],
      data2: [],
    } as ChartConfigModel;
    if (data && data.series && data.series.length) {
      data.series.forEach((x) => {
        model.labels.push(x.key);
        model.data.push(x.value);
        if (x.value1 != null) {
          model.data1.push(x.value1);
        }
        if (x.value2 != null) {
          model.data2.push(x.value2);
        }
      });
    }
    if (data && data.previousYearSeries && data.previousYearSeries?.length) {
      data.previousYearSeries?.forEach((x) => {
        model.labels.push(x.key);
        model.data.push(x.value);
        if (x.value1 != null) {
          model.data1.push(x.value1);
        }
        if (x.value2 != null) {
          model.data2.push(x.value2);
        }
      });
    }
    return model;
  }
}
